import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useI18n } from '../i18n/I18nProvider';
import UnifiedPracticeResult from '../components/UnifiedPracticeResult';
import {
  buildPracticeHistoryPath,
  fetchPracticeAttemptDetail,
  startPracticeAgain,
} from '../lib/practiceAttempts';

const errorText = (error, t) => {
  if (error?.status === 401 || error?.status === 403) return t('You do not have access to this practice result.');
  if (error?.status === 404) return t('This practice result was not found.');
  return error?.message ? t(error.message) : t('The practice result could not be loaded.');
};

export default function PracticeAttemptDetailPage({ viewer = 'student' }) {
  const { t } = useI18n();
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!attemptId) return;
    setLoading(true);
    setError('');
    try {
      setDetail(await fetchPracticeAttemptDetail(attemptId, viewer));
    } catch (loadError) {
      setDetail(null);
      setError(errorText(loadError, t));
    } finally {
      setLoading(false);
    }
  }, [attemptId, t, viewer]);

  useEffect(() => { load(); }, [load]);

  const practiceAgain = async () => {
    const assignmentId = detail?.assignment?.id || detail?.attempt?.assignment_id;
    if (!assignmentId || starting) return;
    setStarting(true);
    setError('');
    try {
      navigate(await startPracticeAgain(assignmentId));
    } catch (startError) {
      setError(errorText(startError, t));
    } finally {
      setStarting(false);
    }
  };

  if (loading) return <p className="p-8 text-center text-gray-600">{t('Loading practice result...')}</p>;
  if (error || !detail) {
    return (
      <main className="mx-auto max-w-3xl p-4 md:p-8">
        <div className="border border-rose-200 bg-rose-50 px-5 py-4 text-rose-800">{error || t('The practice result could not be loaded.')}</div>
        <button type="button" onClick={load} className="mt-4 inline-flex items-center gap-2 rounded border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50">
          <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
          {t('Try again')}
        </button>
      </main>
    );
  }

  const assignmentId = detail.assignment?.id || detail.attempt?.assignment_id;
  return (
    <UnifiedPracticeResult
      detail={detail}
      viewer={viewer}
      onPracticeAgain={practiceAgain}
      starting={starting}
      historyPath={buildPracticeHistoryPath(assignmentId, viewer)}
    />
  );
}
